/**
 * Utility which computes whether a target window and {@link window.top} conform to the same origin policy.
 *
 * @description If they do, then they share JavaScript execution contexts.
 * @description If they do not, they are the traditional cross-origin `iframe` and host.
 *
 * @param window
 * @returns true if same origin, false if not - if true, then {@link window.top} must be defined.
 */
export const isSameOrigin = (window: Window): window is Window & { top: Window } => {
	try {
		return Boolean(window.origin && window.origin === window.top?.origin);
	} catch {
		return false;
	}
};

/**
 * Utility which computes whether a given window is the top window.
 * @param window window under analysis
 * @returns true if {@link window.top}, otherwise false
 */
export const isTop = (window: Window): boolean => {
	try {
		return Boolean(window.top && window === window.top);
	} catch {
		return false;
	}
};

/**
 * Maximum depth to traverse when finding topmost same-origin window.
 * Prevents infinite loops in edge cases while supporting deep nesting.
 */
const MAX_PARENT_TRAVERSAL_DEPTH = 16;

/**
 * Finds the topmost same-origin window by walking up the parent chain.
 * Stops when it hits a cross-origin boundary or reaches the top.
 * @param window window under analysis
 * @returns the topmost same-origin window
 */
export const findTopmostSameOriginWindow = (window: Window): Window => {
	let current = window;
	let depth = 0;

	while (depth < MAX_PARENT_TRAVERSAL_DEPTH) {
		try {
			// If we're at the top, return current
			if (current.parent === current) {
				return current;
			}

			// Try to access parent.origin (will throw SecurityError if cross-origin)
			const parentOrigin = current.parent.origin;

			// Check if parent is same origin
			if (current.origin !== parentOrigin) {
				return current;
			}

			// Parent is same-origin, keep going up
			current = current.parent;
			depth++;
		} catch {
			// Parent is cross-origin (accessing parent.origin threw)
			return current;
		}
	}

	// Safety: return current window if max depth reached
	return current;
};
